import { CalendarPlus, Download, MapPin, Ticket, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { googleCalendarUrl, icsUrl, posterFor, typeMeta } from "../lib/calendar";

const fmt = (iso, opts) => new Date(iso).toLocaleString("en-GB", opts);

export function EventDialog({ event, open, onOpenChange }) {
  if (!event) return null;
  const meta = typeMeta(event.event_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="event-dialog"
        className="glass max-w-lg rounded-md text-white border-white/10 p-0 overflow-hidden max-h-[92vh] overflow-y-auto scrollbar-thin"
      >
          <div className="relative h-40 w-full overflow-hidden">
            <img
              src={posterFor(event)}
              alt={event.title}
              data-testid="event-dialog-poster"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            {event.sold_out && (
              <span className="absolute top-3 right-3 px-2 py-1 rounded-sm bg-destructive text-white text-[10px] font-mono font-bold uppercase tracking-[0.2em]">
                Sold out
              </span>
            )}
          </div>
        <div className="h-1.5 w-full" style={{ background: meta.color }} />
        <div className="p-6">
          <DialogHeader>
            <span
              className="text-[11px] font-mono uppercase tracking-[0.2em] w-fit px-2 py-0.5 rounded-sm"
              style={{ color: meta.color, border: `1px solid ${meta.color}55` }}
              data-testid="event-dialog-type"
            >
              {meta.label} · {event.game}
            </span>
            <DialogTitle className="font-display text-2xl tracking-tighter pt-2" data-testid="event-dialog-title">
              {event.title}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-2 text-sm text-neutral-300">
            <p className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary shrink-0" />
              {fmt(event.start, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
            </p>
            {event.location && (
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary shrink-0" /> {event.location}
              </p>
            )}
            {event.price && (
              <p className="flex items-center gap-2 font-mono">
                <Ticket className="h-4 w-4 text-primary shrink-0" /> {event.price}
              </p>
            )}
          </div>

          {event.description && (
            <p className="mt-4 text-sm text-neutral-400 whitespace-pre-wrap">{event.description}</p>
          )}

          <div className="mt-6 space-y-2">
            {event.sold_out && (
              <div
                data-testid="event-dialog-sold-out"
                className="w-full text-center py-2.5 rounded-full border border-destructive/50 bg-destructive/10 text-destructive font-bold uppercase tracking-wider text-sm"
              >
                Sold out
              </div>
            )}
            {event.ticket_url && !event.sold_out && (
              <a href={event.ticket_url} target="_blank" rel="noreferrer" className="block" data-testid="event-buy-tickets">
                <Button className="w-full bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D] hover:scale-[1.02] transition-transform duration-300">
                  <Ticket className="h-4 w-4 mr-2" /> Buy Tickets
                </Button>
              </a>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <a href={googleCalendarUrl(event)} target="_blank" rel="noreferrer" data-testid="event-google-calendar">
                <Button variant="outline" className="w-full rounded-full border-white/20 hover:border-primary hover:text-primary transition-colors duration-200">
                  <CalendarPlus className="h-4 w-4 mr-2" /> Google Calendar
                </Button>
              </a>
              <a href={icsUrl(event)} data-testid="event-ics-download">
                <Button variant="outline" className="w-full rounded-full border-white/20 hover:border-primary hover:text-primary transition-colors duration-200">
                  <Download className="h-4 w-4 mr-2" /> .ics File
                </Button>
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EventDialog;
